import type { PropertyDetail } from '@/types/property'
import { PATHS } from '@/routes/paths'
import { buildPropertyHref } from '@/lib/propertyNavLinks'
import {
  absoluteUrl,
  isValidPropertyCanonical,
  polishPropertyMetaDescription,
  propertyDetailPath,
  titleCase,
  truncateMetaDescription,
  uniqueKeywords,
} from '../utils'
import { breadcrumbSchema } from '../schemas/common'

function extractFurnished(property: PropertyDetail): string | null {
  const fromOption = property.options?.find((o) =>
    /furnish/i.test(`${o.group ?? ''} ${o.name}`),
  )?.value
  if (fromOption) return titleCase(fromOption)
  const fromSpec = property.specs?.find((s) => /furnish/i.test(s.label))?.value
  if (fromSpec) return titleCase(fromSpec)
  const fromOverview = property.overview?.find((o) => /furnish/i.test(o.label))?.value
  if (fromOverview) return titleCase(fromOverview)
  return null
}

function purposeLabel(intent?: PropertyDetail['intent']) {
  if (intent === 'rent') return 'Rent'
  if (intent === 'commercial') return 'Lease'
  return 'Sale'
}

function displayCity(city?: string | null): string {
  return titleCase(city) || 'India'
}

/** Meta title leads with property name (admin DB title wins when set). */
export function buildPropertySeoTitle(property: PropertyDetail): string {
  const name = property.name?.trim() || 'Property'
  const category = titleCase(property.category) || 'Property'
  const city = displayCity(property.city)
  const purpose =
    property.intent === 'rent'
      ? 'for Rent'
      : property.intent === 'commercial'
        ? 'for Lease'
        : 'for Sale'
  const bhkPart = property.bhk ? `${property.bhk} ` : ''

  return `${name} | ${bhkPart}${category} ${purpose} in ${city} | Quick Build Con`
}

/** Prefer polished property description; template fallback when empty. */
export function buildPropertySeoDescription(property: PropertyDetail): string {
  const fromDescription = property.description
    ? polishPropertyMetaDescription(property.description)
    : ''
  if (fromDescription) {
    return fromDescription
  }

  const purpose = purposeLabel(property.intent).toLowerCase()
  const type = titleCase(property.category) || 'property'
  const furnished = extractFurnished(property)
  const areaPart = property.area ? `${property.area}, ` : ''
  const location = `${areaPart}${property.location || displayCity(property.city)}`.replace(/,\s*$/, '')
  const sizePart = property.areaSize?.label
    ? ` ${property.areaSize.label}`
    : property.sqft
      ? ` ${property.sqft} sq.ft`
      : ''
  const builderPart = property.builder ? ` by ${property.builder}` : ''

  const auto = `${purpose === 'sale' ? 'Buy' : purpose === 'rent' ? 'Rent' : 'Explore'} this ${property.bhk ? `${property.bhk} ` : ''}${furnished ? `${furnished.toLowerCase()} ` : ''}${type.toLowerCase()} at ${property.name}, ${location}.${sizePart}${builderPart}. View photos, amenities, price and contact on Quick Build Con.`

  return truncateMetaDescription(auto)
}

export function buildPropertySeoKeywords(property: PropertyDetail): string {
  const city = (property.city || 'india').toLowerCase()
  const area = property.area?.toLowerCase()
  const type = (property.category || 'property').toLowerCase()
  const purpose = property.intent === 'rent' ? 'rent' : 'sale'
  return uniqueKeywords([
    property.bhk ? `${property.bhk.toLowerCase()} ${type} ${city}` : null,
    `${type} for ${purpose} ${city}`,
    property.name.toLowerCase(),
    area ? `${area} property` : null,
    extractFurnished(property)?.toLowerCase(),
    property.builder?.toLowerCase(),
    'qbc verified property',
    `real estate ${city}`,
    ...(property.amenities?.slice(0, 5).map((a) => (typeof a === 'string' ? a : a.name).toLowerCase()) ?? []),
  ])
}

export function propertyListingSchema(property: PropertyDetail) {
  const url = absoluteUrl(propertyDetailPath(property.slug))
  const price = typeof property.price === 'number' ? property.price : undefined
  const images = Array.from(new Set([property.image, ...(property.images ?? [])].filter(Boolean))).slice(0, 10)
  const postedMs = property.postedAt ? Date.parse(property.postedAt) : NaN

  return {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    name: property.name,
    description: buildPropertySeoDescription(property),
    url,
    image: images.length ? images : undefined,
    datePosted: Number.isFinite(postedMs) ? new Date(postedMs).toISOString() : undefined,
    // Locality belongs in streetAddress; addressRegion is the state (not available on the payload).
    address: {
      '@type': 'PostalAddress',
      streetAddress: property.address?.trim() || property.area || property.location || undefined,
      addressLocality: displayCity(property.city),
      addressCountry: 'IN',
    },
    geo:
      property.lat != null && property.lng != null
        ? { '@type': 'GeoCoordinates', latitude: property.lat, longitude: property.lng }
        : undefined,
    offers: price
      ? {
          '@type': 'Offer',
          price,
          priceCurrency: 'INR',
          availability: 'https://schema.org/InStock',
          url,
        }
      : undefined,
  }
}

export function propertyBreadcrumbSchema(property: PropertyDetail) {
  const city = displayCity(property.city)
  const citySlug = property.city.trim().toLowerCase().replace(/\s+/g, '-')
  return breadcrumbSchema([
    { name: 'Home', url: PATHS.home },
    { name: city, url: buildPropertyHref('', { city: citySlug }) },
    ...(property.area
      ? [{
          name: property.area,
          url: buildPropertyHref('', { city: citySlug, area: property.area }),
        }]
      : []),
    { name: property.name, url: propertyDetailPath(property.slug) },
  ])
}

export function propertyDetailSchemas(property: PropertyDetail) {
  return [propertyListingSchema(property), propertyBreadcrumbSchema(property)]
}

/**
 * Canonical is always /property/{slug}.
 * DB canonical is used only when it already points at this property URL.
 */
export function propertyCanonicalUrl(property: PropertyDetail, adminCanonical?: string | null): string {
  const generated = absoluteUrl(propertyDetailPath(property.slug))
  const admin = adminCanonical?.trim()
  if (admin && isValidPropertyCanonical(admin, property.slug)) {
    return absoluteUrl(admin)
  }
  return generated
}

export function propertyOgTitle(property: PropertyDetail): string {
  return buildPropertySeoTitle(property)
}

/** Fallback SEO from URL slug before property API data is available. */
export function buildPropertySeoFromSlug(slug: string) {
  const stripped = slug.replace(/^\d+-/, '')
  const human = titleCase(stripped.replace(/-/g, ' ')) || 'Property'
  const isRent = /\bfor-rent\b|\bfor rent\b/i.test(stripped)
  const purpose = isRent ? 'for Rent' : 'for Sale'

  return {
    title: `${human} | ${purpose} Property | Quick Build Con`,
    description: truncateMetaDescription(
      `Explore ${human} on Quick Build Con. View photos, price, location, amenities and contact for verified ${isRent ? 'rent' : 'sale'} listings.`,
    ),
    canonical: absoluteUrl(propertyDetailPath(slug)),
  }
}
